import { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Heart, ArrowLeft } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useFavoriteStore } from '@/stores/favoriteStore';
import { usePromptStore } from '@/stores/promptStore';
import { PromptList } from '@/components/prompt/PromptList';
import { PromptDetail } from '@/components/prompt/PromptDetail';
import { ThemeToggle } from '@/components/ThemeToggle';
import type { Prompt } from '@/types/prompt';

export default function FavoritesPage() {
  const navigate = useNavigate();
  const { favorites } = useFavoriteStore();
  const { prompts, isLoading, loadPrompts } = usePromptStore();
  const [selectedPrompt, setSelectedPrompt] = useState<Prompt | null>(null);
  const [isDetailOpen, setIsDetailOpen] = useState(false);

  // 加载所有 Prompt（用于筛选收藏）
  useEffect(() => {
    loadPrompts();
  }, [loadPrompts]);

  // 筛选出收藏的 Prompt
  const favoritePrompts = useMemo(() => {
    return prompts.filter((p) => favorites.includes(p.id));
  }, [prompts, favorites]);

  const handleViewDetail = (prompt: Prompt) => {
    setSelectedPrompt(prompt);
    setIsDetailOpen(true);
  };

  const handleCloseDetail = () => {
    setIsDetailOpen(false);
    setTimeout(() => setSelectedPrompt(null), 300);
  };

  return (
    <div className="relative min-h-screen">
      {/* 头部导航 */}
      <header className="glass border-b border-border/50 backdrop-blur-md sticky top-0 z-40">
        <div className="container mx-auto flex h-16 items-center justify-between px-4">
          <h1 className="text-xl md:text-2xl font-bold">我的收藏</h1>
          <div className="flex items-center gap-2 md:gap-4">
            <ThemeToggle />
            <Button variant="outline" size="sm" onClick={() => navigate('/')}>
              <ArrowLeft className="h-4 w-4 md:mr-2" />
              <span className="hidden md:inline">返回首页</span>
            </Button>
          </div>
        </div>
      </header>

      {/* 主内容区 */}
      <main className="container mx-auto p-4 md:p-8">
        {/* 统计信息 */}
        <div className="mb-6">
          <div className="text-sm text-muted-foreground">
            共收藏 <span className="font-semibold">{favoritePrompts.length}</span> 个 Prompt
          </div>
        </div>

        {/* Prompt 列表 */}
        {favoritePrompts.length === 0 ? (
          <div className="glass-card floating-shadow p-8 md:p-16 text-center">
            <div className="flex justify-center mb-4">
              <Heart className="w-12 h-12 md:w-16 md:h-16 text-muted-foreground" />
            </div>
            <h3 className="text-lg md:text-xl font-semibold mb-2">
              还没有收藏任何 Prompt
            </h3>
            <p className="text-sm md:text-base text-muted-foreground mb-6">
              浏览 Prompt 库并收藏你喜欢的内容
            </p>
            <Button onClick={() => navigate('/')}>
              浏览 Prompt
            </Button>
          </div>
        ) : (
          <PromptList
            prompts={favoritePrompts}
            onViewDetail={handleViewDetail}
            isLoading={isLoading}
          />
        )}
      </main>

      {/* Prompt 详情模态框 */}
      <PromptDetail
        prompt={selectedPrompt}
        isOpen={isDetailOpen}
        onClose={handleCloseDetail}
      />
    </div>
  );
}

